import { Button } from '@workspace/ui/components/button'
import { cn } from '@workspace/ui/lib/utils'
import { ArrowLeft, ArrowRight, FileText, Maximize2, Minimize2 } from 'lucide-react'
import * as pdfjs from 'pdfjs-dist'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'

type FitMode = 'page' | 'width'

export interface PdfViewerProps {
  url: string
  className?: string
  footerAction?: React.ReactNode
  canvasRef?: React.RefObject<HTMLCanvasElement | null>
  /** When true, renders at 720p for streaming regardless of container size */
  streamingHeight?: number
  /** Called when the visible page changes (navigation or initial load). */
  onPageChange?: (page: number, totalPages: number) => void
}

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

type PDFDocumentProxy = Awaited<ReturnType<typeof pdfjs.getDocument>['promise']>

export function PdfViewer({
  url,
  className,
  footerAction,
  canvasRef: externalCanvasRef,
  streamingHeight,
  onPageChange,
}: PdfViewerProps) {
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const [fitMode, setFitMode] = useState<FitMode>('page')

  const containerRef = useRef<HTMLDivElement>(null)
  const internalCanvasRef = useRef<HTMLCanvasElement>(null)
  const canvasRef = externalCanvasRef ?? internalCanvasRef
  const renderTaskRef = useRef<ReturnType<pdfjs.PDFPageProxy['render']> | null>(null)
  const onPageChangeRef = useRef(onPageChange)
  onPageChangeRef.current = onPageChange

  // Observe container size
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        const { width, height } = entry.contentRect
        setContainerSize({ width, height })
      }
    })

    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [])

  // Load PDF document
  useEffect(() => {
    let cancelled = false

    setIsLoading(true)
    setError(null)
    setPdfDoc(null)
    setPageNumber(1)

    pdfjs
      .getDocument(url)
      .promise.then((doc) => {
        if (!cancelled) {
          setPdfDoc(doc)
          setIsLoading(false)
          onPageChangeRef.current?.(1, doc.numPages)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load PDF')
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [url])

  // Render page to canvas
  // biome-ignore lint/correctness/useExhaustiveDependencies: canvasRef is a stable RefObject
  useEffect(() => {
    const canvas = canvasRef.current
    if (!pdfDoc || !canvas || containerSize.width === 0) return

    let cancelled = false

    if (renderTaskRef.current) {
      renderTaskRef.current.cancel()
      renderTaskRef.current = null
    }

    pdfDoc.getPage(pageNumber).then((page) => {
      if (cancelled) return

      const unscaledViewport = page.getViewport({ scale: 1 })
      let scale: number

      if (streamingHeight) {
        // In streaming mode, render at fixed height
        scale = streamingHeight / unscaledViewport.height
      } else {
        const scaleX = containerSize.width / unscaledViewport.width
        if (fitMode === 'width') {
          // Fit to container width, allow vertical scroll
          scale = scaleX
        } else {
          // Fit entire page in container
          const scaleY = containerSize.height / unscaledViewport.height
          scale = Math.min(scaleX, scaleY)
        }
      }

      const dpr = window.devicePixelRatio || 1
      const viewport = page.getViewport({ scale })
      const hiResViewport = page.getViewport({ scale: scale * dpr })

      canvas.width = hiResViewport.width
      canvas.height = hiResViewport.height
      canvas.style.width = `${viewport.width}px`
      canvas.style.height = `${viewport.height}px`

      const ctx = canvas.getContext('2d', { willReadFrequently: true })
      if (!ctx) return

      const renderTask = page.render({
        canvasContext: ctx,
        viewport: hiResViewport,
        canvas: canvas,
      })

      renderTaskRef.current = renderTask

      renderTask.promise.catch((err) => {
        if (err.name !== 'RenderingCancelledException') {
          console.error('PDF render error:', err)
        }
      })
    })

    return () => {
      cancelled = true
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel()
        renderTaskRef.current = null
      }
    }
  }, [pdfDoc, pageNumber, containerSize, streamingHeight, fitMode])

  const goToPreviousPage = useCallback(() => {
    setPageNumber((prev) => {
      const next = Math.max(prev - 1, 1)
      if (pdfDoc) onPageChangeRef.current?.(next, pdfDoc.numPages)
      return next
    })
    containerRef.current?.scrollTo(0, 0)
  }, [pdfDoc])

  const goToNextPage = useCallback(() => {
    if (!pdfDoc) return
    setPageNumber((prev) => {
      const next = Math.min(prev + 1, pdfDoc.numPages)
      onPageChangeRef.current?.(next, pdfDoc.numPages)
      return next
    })
    containerRef.current?.scrollTo(0, 0)
  }, [pdfDoc])

  useHotkeys('left', goToPreviousPage)
  useHotkeys('right', goToNextPage)

  const numPages = pdfDoc?.numPages ?? null

  return (
    <div className={cn('flex h-full min-h-0 flex-col bg-zinc-900', className)}>
      <div
        ref={containerRef}
        className={cn(
          'relative flex min-h-0 flex-1 justify-center',
          fitMode === 'width' ? 'items-start overflow-y-auto' : 'items-center overflow-hidden'
        )}
      >
        {isLoading && (
          <div className="absolute flex flex-col items-center gap-3 text-muted-foreground">
            <FileText className="size-12 animate-pulse" />
            <span>Loading document...</span>
          </div>
        )}

        {error && (
          <div className="absolute flex flex-col items-center gap-3 text-muted-foreground">
            <FileText className="size-12" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        <canvas
          ref={canvasRef}
          className="shadow-2xl"
          style={
            streamingHeight
              ? { maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }
              : undefined
          }
        />
      </div>

      {(numPages && numPages > 0) || footerAction ? (
        <div
          className={cn(
            'flex items-center border-t border-zinc-700 bg-zinc-800/80 px-4 py-2 backdrop-blur-sm',
            footerAction ? 'justify-between' : 'justify-center'
          )}
        >
          {numPages && numPages > 0 ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousPage}
                disabled={pageNumber <= 1}
                className="size-8 p-0 focus-visible:ring-0 focus-visible:border-border"
              >
                <ArrowLeft className="size-4" />
              </Button>

              <span className="min-w-[60px] text-center text-xs font-medium text-muted-foreground">
                {pageNumber} / {numPages}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                className="size-8 p-0 focus-visible:ring-0 focus-visible:border-border"
              >
                <ArrowRight className="size-4" />
              </Button>

              {!streamingHeight && (
                <>
                  <div className="ml-1 h-4 w-px bg-zinc-600" />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFitMode((m) => (m === 'page' ? 'width' : 'page'))}
                    className="size-8 p-0 focus-visible:ring-0 focus-visible:border-border"
                    title={fitMode === 'page' ? 'Full width' : 'Full page'}
                  >
                    {fitMode === 'page' ? (
                      <Maximize2 className="size-4" />
                    ) : (
                      <Minimize2 className="size-4" />
                    )}
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div />
          )}

          {footerAction}
        </div>
      ) : null}
    </div>
  )
}
